import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { ArrowUpRight, ArrowLeft, ChevronRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const CATEGORIES = [
  {
    id: "getting-started",
    title: "Getting started",
    faqs: [
      {
        q: "Do I need a north-facing roof for solar in Taranaki?",
        a: "North-facing is ideal in New Zealand because it captures the most sunlight, but east and west-facing roofs also work well — typically producing 80–90% of the yield of a north-facing array. We model your specific roof orientation and shading before quoting.",
      },
      {
        q: "How much sun does New Plymouth get?",
        a: "Taranaki averages around 2,180 sunshine hours a year — very productive for solar. Coastal areas like New Plymouth, Bell Block and Ōakura benefit from clear, unshaded skies most of the year, and even on overcast days modern panels produce meaningful power.",
      },
      {
        q: "Is my roof suitable for solar?",
        a: "Most residential roofs in Taranaki are suitable, including corrugated iron, long-run steel and concrete tile. We assess pitch, orientation, shading, roof age and structural integrity during our free site assessment. Very old or damaged roofs may need replacement first.",
      },
      {
        q: "Do I need council consent for solar panels?",
        a: "In most cases residential solar installations in New Plymouth District do not require resource or building consent because they qualify as permitted activity under the district plan. Some heritage or coastal-overlay properties may need consent — we check this for you before starting.",
      },
      {
        q: "How do I get started?",
        a: "Request a free quote via our website form or email admin@centralsolar.co.nz. We'll ask a few questions about your power usage and roof, then arrange a free on-site assessment. From there you'll receive a full system design and transparent quote.",
      },
    ],
  },
  {
    id: "cost-savings",
    title: "Cost & savings",
    faqs: [
      {
        q: "How much does a solar system cost in New Plymouth?",
        a: "Every home and business is different — roof, orientation, power usage, panel and inverter choice, and whether you add a battery all shift the price. Rather than quote a generic figure, we build every quote from scratch after a free on-site assessment. Get in touch and we'll give you honest, tailored numbers for your place.",
      },
      {
        q: "How much can I save on my power bill?",
        a: "Most New Plymouth homes save 40–70% off their power bill with solar alone, and 70–95% once a battery is added. Actual savings depend on your usage pattern, system size, and how much power you use during daylight hours.",
      },
      {
        q: "What's the payback period on solar in Taranaki?",
        a: "Typical payback is 5–8 years for a solar-only system and 7–10 years with a battery, based on current NZ electricity prices. After payback everything the system produces is essentially free power for the remaining 15–20+ year system life.",
      },
      {
        q: "Are there any government subsidies or grants for solar in NZ?",
        a: "As of 2026 there are no direct government subsidies for residential solar in New Zealand. However, some banks offer preferential green home loans at reduced interest rates, and Warmer Kiwi Homes (EECA) subsidies apply to insulation and heat pumps — often installed alongside solar.",
      },
      {
        q: "Can I finance my solar system?",
        a: "Yes. Options include green home loans from ANZ, ASB, BNZ, Kiwibank and Westpac at reduced interest rates, adding to your mortgage, or third-party solar finance providers. We can point you in the right direction based on your situation.",
      },
    ],
  },
  {
    id: "installation",
    title: "Installation process",
    faqs: [
      {
        q: "How long does a solar installation take?",
        a: "Typical installation time is within 1 week from start to switch-on. The physical rooftop work is usually completed in 1–2 days; the remaining time covers commissioning, meter changes and Powerco grid approval.",
      },
      {
        q: "Do I need to be home during installation?",
        a: "Not usually. We need access to the roof, meter box and the inside of your home briefly for the inverter install. Most clients are at work while we install — we simply need arrangements for site access and someone available to answer any quick questions.",
      },
      {
        q: "Will installation damage my roof?",
        a: "No. We use certified Clenergy mounting systems designed for corrugated iron and long-run roofing, with proper flashings and sealants. Every mounting point is weathertight and installed to New Zealand standards. Our workmanship is guaranteed for 10 years.",
      },
      {
        q: "Do I get monitoring for my solar system?",
        a: "Yes. Every system we install includes real-time production and consumption monitoring via a smartphone app, so you can see exactly how much solar you're generating, using, exporting and (if you have batteries) storing.",
      },
    ],
  },
  {
    id: "batteries",
    title: "Batteries & storage",
    faqs: [
      {
        q: "Do I need a battery with my solar system?",
        a: "Not necessarily. Solar without a battery still delivers strong savings if you use most of your power during the day (dishwasher, laundry, hot water on timer, EV charging). Batteries make sense if you use more power at night, want backup during outages, or want to maximise self-consumption.",
      },
      {
        q: "Which battery brands do you install?",
        a: "We install Tesla Powerwall, Sungrow, BYD, Sig Energy and Fronius Reserva. Each has strengths — we recommend based on your usage pattern, backup needs and budget, not based on which brand we're pushing.",
      },
      {
        q: "How long do solar batteries last?",
        a: "Modern lithium batteries are warranted for 10 years with 70%+ retained capacity, and real-world life is typically 12–15 years. Batteries do gradually lose capacity over time — similar to a laptop or phone battery, but much slower.",
      },
      {
        q: "Will my battery power the whole house in an outage?",
        a: "It depends on battery size and how it's wired. A Tesla Powerwall 3 will run essential loads (fridge, lights, Wi-Fi, comms) for 12–24 hours easily, and can be configured for whole-home backup. Smaller batteries typically back up only essential circuits.",
      },
    ],
  },
  {
    id: "grid-connection",
    title: "Grid connection & metering",
    faqs: [
      {
        q: "Who is my lines company in Taranaki?",
        a: "Powerco is the lines company for New Plymouth, Waitara, Bell Block, Inglewood, Ōakura, Stratford, Hāwera and most of Taranaki. We handle the entire Powerco distributed-generation approval process on your behalf.",
      },
      {
        q: "Do I need permission from Powerco to install solar?",
        a: "Yes. Any grid-connected solar system must be approved by Powerco before it's energised. This is called a Distributed Generation (DG) application. We prepare and submit the paperwork, and approval typically takes 2–6 weeks.",
      },
      {
        q: "Will I get paid for exporting solar back to the grid?",
        a: "Yes. Most NZ retailers offer a buy-back rate for exported solar power — typically 8–17c per kWh depending on the retailer and plan. Rates change often, so we help you compare current plans before switch-on and again once you've been running for a few months.",
      },
      {
        q: "Can I go completely off-grid in Taranaki?",
        a: "Yes, but it's usually cheaper and more resilient to stay grid-tied unless your property is remote. For rural properties without power lines nearby, a purpose-designed off-grid system with solar + batteries + backup generator is often more economical than paying for a new grid connection.",
      },
    ],
  },
  {
    id: "maintenance",
    title: "Maintenance & warranty",
    faqs: [
      {
        q: "Do solar panels need cleaning?",
        a: "In Taranaki, rain generally does an excellent job of keeping panels clean. We recommend a visual check once a year and occasional cleaning if you're near a farm, unsealed road, or under trees. We offer optional annual maintenance visits.",
      },
      {
        q: "What warranty do I get?",
        a: "Warranty periods vary by manufacturer and model. Typically you'll get 25 years performance warranty on panels, 10–15 years on inverters (often extendable), and 10 years on batteries — plus our own 10-year workmanship guarantee on the physical installation. We confirm the exact warranty periods for your chosen products in your quote, and hand over all paperwork at commissioning.",
      },
      {
        q: "What if my system stops working?",
        a: "Give us a call — we're on call and ready to help fix the issue.",
      },
    ],
  },
];

const allFaqs = CATEGORIES.flatMap((c) =>
  c.faqs.map((f) => ({ ...f, category: c.title, categoryId: c.id }))
);

const FAQ = () => {
  const [query, setQuery] = useState("");

  useEffect(() => {
    document.title = "FAQ · Central Solar | New Plymouth Solar Questions Answered";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Common questions about solar panel installations, batteries, grid connection and costs in New Plymouth and Taranaki, answered by Central Solar."
      );
    }
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
    return () => {
      document.title = "Central Solar | Solar Panel Installers in New Plymouth & Taranaki, NZ";
    };
  }, []);

  // Inject FAQPage JSON-LD schema for Google FAQ rich snippets
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: allFaqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "faq-jsonld";
    el.text = JSON.stringify(schema);
    document.head.appendChild(el);
    return () => {
      document.getElementById("faq-jsonld")?.remove();
    };
  }, []);

  const q = query.trim().toLowerCase();
  const filteredCategories = q
    ? CATEGORIES.map((c) => ({
        ...c,
        faqs: c.faqs.filter(
          (f) =>
            f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)
        ),
      })).filter((c) => c.faqs.length > 0)
    : CATEGORIES;

  const totalHits = filteredCategories.reduce((n, c) => n + c.faqs.length, 0);

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="faq-page">
      <Navigation />

      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src="/kaitake-ranges.png"
            alt=""
            aria-hidden
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#F7F6F2]/90 via-[#F7F6F2]/80 to-[#F7F6F2]" />
        </div>

        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            data-testid="faq-back-home"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#5C6B61] hover:text-[#1A3626] transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to home
          </Link>

          <div className="text-xs uppercase tracking-[0.3em] text-[#5C6B61] mt-8">
            · Frequently asked
          </div>
          <h1
            data-testid="faq-heading"
            className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-medium mt-6 text-[#1A3626] leading-[1.02]"
          >
            Solar questions,{" "}
            <span className="italic font-light">answered</span> for Taranaki.
          </h1>
          <p className="mt-6 text-lg text-[#5C6B61] max-w-2xl leading-relaxed">
            Everything New Plymouth homeowners ask us about solar
            installations, batteries, Powerco grid connections, costs and
            timelines. Can&rsquo;t find what you need? Just ask.
          </p>

          <div className="mt-10 relative max-w-xl">
            <Search className="h-4 w-4 text-[#5C6B61] absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              data-testid="faq-search"
              placeholder="Search the FAQ…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-14 pl-11 rounded-full border-[#D2D7D0] bg-white/80 backdrop-blur focus-visible:ring-[#1A3626]"
            />
          </div>
        </div>
      </section>

      <section className="pb-24 lg:pb-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          {q && (
            <div
              data-testid="faq-search-count"
              className="text-sm text-[#5C6B61] mb-8"
            >
              {totalHits} {totalHits === 1 ? "result" : "results"} for &ldquo;
              {query}
              &rdquo;
            </div>
          )}

          {filteredCategories.length === 0 && (
            <div className="text-center py-24">
              <p className="text-[#5C6B61]">
                No results — try a different word, or ask us directly at{" "}
                <a
                  href="mailto:admin@centralsolar.co.nz"
                  className="text-[#1A3626] underline"
                >
                  admin@centralsolar.co.nz
                </a>
                .
              </p>
            </div>
          )}

          <div className="space-y-16">
            {filteredCategories.map((cat, ci) => (
              <div key={cat.id} data-testid={`faq-category-${cat.id}`}>
                <div className="flex items-baseline gap-4 mb-6">
                  <span className="font-display text-2xl text-[#F5C242]">
                    0{ci + 1}
                  </span>
                  <h2 className="font-display text-2xl sm:text-3xl tracking-tight text-[#1A3626]">
                    {cat.title}
                  </h2>
                </div>

                <div className="space-y-3">
                  {cat.faqs.map((f, i) => (
                    <details
                      key={f.q}
                      data-testid={`faq-item-${cat.id}-${i + 1}`}
                      className="group rounded-2xl bg-white border border-[#D2D7D0] p-6 open:border-[#1A3626]/40 open:shadow-[0_8px_32px_rgba(26,54,38,0.05)] transition-shadow"
                    >
                      <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                        <span className="font-display text-lg text-[#1A3626] leading-snug">
                          {f.q}
                        </span>
                        <ChevronRight className="h-5 w-5 text-[#5C6B61] mt-1 shrink-0 transition-transform group-open:rotate-90" />
                      </summary>
                      <p className="mt-4 text-sm sm:text-base text-[#5C6B61] leading-relaxed">
                        {f.a}
                      </p>
                    </details>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#1A3626] text-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-[#F5C242]">
            · Still curious?
          </div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-medium mt-4 leading-[1.05]">
            Ask us anything about solar in Taranaki.
          </h2>
          <p className="mt-5 text-white/75">
            A quick email or a free site visit — whichever suits.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Button
              data-testid="faq-cta-quote"
              asChild
              className="rounded-full bg-[#F5C242] text-[#1A3626] hover:bg-[#DEAA2B] h-14 px-8 text-base group"
            >
              <Link to="/#quote">
                Request a quote
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </Button>
            <a
              data-testid="faq-cta-email"
              href="mailto:admin@centralsolar.co.nz"
              className="text-sm uppercase tracking-[0.2em] text-white/80 hover:text-white"
            >
              admin@centralsolar.co.nz
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQ;
